import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Paper, Typography, TextField, Button,
    Alert, CircularProgress, InputAdornment, IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axiosInstance from "./Axiosinstance";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [newPassword,     setNewPassword]     = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew,         setShowNew]         = useState(false);
    const [showConfirm,     setShowConfirm]     = useState(false);
    const [error,           setError]           = useState("");
    const [success,         setSuccess]         = useState(false);
    const [loading,         setLoading]         = useState(false);
    const [tokenValid,      setTokenValid]      = useState(null);

    useEffect(() => {
        if (!token) { setTokenValid(false); return; }
        axiosInstance.get(`/api/auth/validate-token?token=${token}`)
            .then(res => setTokenValid(res.data.data?.valid))
            .catch(() => setTokenValid(false));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) { setError("Password must be at least 8 characters"); return; }
        if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }

        setLoading(true); setError("");
        try {
            await axiosInstance.post("/api/auth/reset-password", { token, newPassword });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || "Reset failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    if (tokenValid === null) return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{
            minHeight: "100vh", display: "flex", alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f0f4ff 0%, #e8f5e9 100%)", px: 2,
        }}>
            <Paper elevation={0} sx={{
                width: "100%", maxWidth: 440, borderRadius: 4,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 8px 40px rgba(0,0,0,0.08)", overflow: "hidden",
            }}>
                <Box sx={{
                    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    px: 4, py: 4, textAlign: "center",
                }}>
                    <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
                        {tokenValid ? "Set New Password" : "Link Expired"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        {tokenValid ? "Choose a strong new password" : "This reset link is no longer valid"}
                    </Typography>
                </Box>

                <Box sx={{ px: 4, py: 4, background: "#fff" }}>
                    {!tokenValid ? (
                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                This link has expired or already been used. Please request a new one.
                            </Typography>
                            <Button fullWidth variant="contained" onClick={() => navigate("/forgot-password")}
                                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
                                Request New Link
                            </Button>
                        </Box>
                    ) : success ? (
                        <Box sx={{ textAlign: "center" }}>
                            <Box sx={{
                                width: 64, height: 64, borderRadius: "50%",
                                background: "#e8f5e9", display: "flex",
                                alignItems: "center", justifyContent: "center",
                                mx: "auto", mb: 2, fontSize: 32,
                            }}>✅</Box>
                            <Typography variant="h6" fontWeight={700} mb={1}>Password Reset!</Typography>
                            <Typography variant="body2" color="text.secondary" mb={3}>
                                Your password has been updated. You can now log in.
                            </Typography>
                            <Button fullWidth variant="contained" onClick={() => navigate("/login")}
                                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600,
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)" }}>
                                Go to Login
                            </Button>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError("")}>
                                    {error}
                                </Alert>
                            )}

                            {[
                                { label: "New Password",     val: newPassword,     set: setNewPassword,     show: showNew,     setShow: setShowNew },
                                { label: "Confirm Password", val: confirmPassword, set: setConfirmPassword, show: showConfirm, setShow: setShowConfirm },
                            ].map(({ label, val, set, show, setShow }) => (
                                <Box key={label} sx={{ mb: 2.5 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>{label}</Typography>
                                    <TextField
                                        fullWidth type={show ? "text" : "password"}
                                        placeholder={`Enter ${label.toLowerCase()}`}
                                        value={val} onChange={(e) => { set(e.target.value); setError(""); }}
                                        disabled={loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShow(!show)} edge="end" size="small" tabIndex={-1}>
                                                        {show ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "rgba(0,0,0,0.02)" } }}
                                    />
                                </Box>
                            ))}

                            <Button type="submit" fullWidth variant="contained" disabled={loading}
                                sx={{
                                    py: 1.5, borderRadius: 2, fontWeight: 600,
                                    textTransform: "none", fontSize: "1rem",
                                    background: "linear-gradient(135deg, #1976d2, #1565c0)",
                                    boxShadow: "0 4px 15px rgba(25,118,210,0.3)",
                                }}>
                                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Reset Password"}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ResetPassword;
